/* eslint-disable no-console */
import posthog, { CapturedNetworkRequest } from 'posthog-js'

import { getUserName } from '@surglogs/models'

import type {
  AnalyticsService,
  AnalyticsServiceInitParams,
  AnalyticsServiceIdentifyUserParams,
  AnalyticsServiceCaptureEventName,
  AnalyticsServiceCaptureEventData,
  AnalyticsServiceSetEventPropertiesParams,
} from '../../types'
import { getJoinedAssignedRoles } from '../RoleService'

export class PostHogService implements AnalyticsService {
  isInitialized = false

  init({
    config,
    release,
    withSessionRecording = true,
  }: AnalyticsServiceInitParams): void {
    if (this.isInitialized) return

    if (!config.posthog.key) {
      console.log('---PostHog no key')

      return
    }

    posthog.init(config.posthog.key, {
      capture_pageview: false,
      autocapture: false,
      api_host: config.posthog.host,
      person_profiles: 'always',
      // DOM sanitization
      mask_all_element_attributes: true,
      mask_all_text: true,
      // network sanitization
      session_recording: {
        maskAllInputs: true,
        maskTextFn: null,
        recordHeaders: true,
        maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
          // Filter the headers, only keeping 'x-correlation-id'
          if (request.responseHeaders) {
            request.responseHeaders = {
              'x-correlation-id': request.responseHeaders['x-correlation-id'],
            }
          }

          // remove request, response body and headers
          request.requestHeaders = undefined
          request.requestBody = undefined
          request.responseBody = undefined

          return request
        },
      },
      capture_performance: {
        web_vitals: false,
      },
      disable_session_recording: !withSessionRecording,
    })

    // no way to pass release version through init
    posthog.register({ release })

    this.isInitialized = true
  }

  identifyUser({ user }: AnalyticsServiceIdentifyUserParams): void {
    if (!this.isInitialized) {
      console.log(
        '---PostHog',
        'cannot identify user because PostHog is not initialized',
      )

      return
    }

    if (user) {
      console.log('---PostHog', 'identify user', user.id)
      posthog.identify(String(user.id), {
        email: String(user.email),
        name: getUserName(user),
      })
    }
  }

  setEventProperties({ data }: AnalyticsServiceSetEventPropertiesParams): void {
    if (!this.isInitialized) {
      console.log(
        '---PostHog',
        'cannot set event properties because PostHog is not initialized',
      )

      return
    }

    const clinic = data?.clinic
    const user = data?.user

    // register function sets properties for all events in that session
    posthog.register({
      clinic_id: clinic?.id,
      clinic_name: clinic?.institution_name,
      clinic_type: clinic?.specialization,
      clinic_accreditation: clinic?.accreditation,
      // user roles change based on clinic, so we need to set them for events made within that clinic, not as user properties on identify
      user_roles: getJoinedAssignedRoles(user?.clinic_roles),
    })

    posthog.capture('$set', {
      $set: { clinic_id: clinic?.id },
    })
  }

  captureEvent<E extends AnalyticsServiceCaptureEventName>(
    name: E,
    data: AnalyticsServiceCaptureEventData<E>,
  ): void {
    if (!this.isInitialized) {
      console.log(
        '---PostHog',
        `cannot capture event ${name} because PostHog is not initialized`,
        data,
      )

      return
    }

    posthog.capture(name, data)
  }

  enableCapturing() {
    if (!this.isInitialized) return

    if (posthog.has_opted_out_capturing()) {
      console.log('---Posthog', 'enabled capturing for user')
      posthog.opt_in_capturing()
    }
  }

  disableCapturing() {
    if (!this.isInitialized) return

    console.log('---Posthog', 'disabled capturing for user')
    posthog.opt_out_capturing()
  }

  enableSessionRecording() {
    if (!posthog.sessionRecordingStarted()) {
      console.log('---Posthog', 'started session recording')
      posthog.startSessionRecording()
    }
  }

  disableSessionRecording() {
    if (!this.isInitialized) return

    console.log('---Posthog', 'disabled session recording for user')
    posthog.sessionRecording?.stopRecording()
  }
}
